import routesEn from "src/locales/en/routes.json";
import routesFr from "src/locales/fr/routes.json";
import { fetchRestaurantPromoBySlug } from "src/api/payload/restaurantPromotions";
import { isPayloadSource } from "src/utils/hubSource";
import {
  isMeaningfulRestaurantPromo,
} from "src/utils/mapRestaurantPromo";

/**
 * Final path segment for `/[fr/]promotions/restaurants/:slug`.
 * @param {string} pathname
 * @returns {string}
 */
export const restaurantPromoSlugFromPathname = (pathname) => {
  if (typeof pathname !== "string" || !pathname.trim()) return "";
  const cleaned = pathname.replace(/\/+$/, "");
  const parts = cleaned.split("/").filter(Boolean);
  return parts[parts.length - 1] || "";
};

/**
 * Match `/promotions/restaurants/:slug` or `/fr/promotions/restaurants/:slug`.
 * @param {string} pathname
 * @returns {{ slug: string, locale: 'en'|'fr' } | null}
 */
export const matchRestaurantPromoPath = (pathname) => {
  if (typeof pathname !== "string" || !pathname.trim()) return null;
  const cleaned = pathname.replace(/\/+$/, "");
  const match = cleaned.match(/^\/(?:(fr)\/)?promotions\/restaurants\/([^/]+)$/);
  if (!match) return null;
  return {
    locale: match[1] === "fr" ? "fr" : "en",
    slug: match[2],
  };
};

/**
 * Resolve a dining promo `promotionKey` from a localized URL slug via routes.json.
 * Used for JSON fallback when Payload is disabled or unavailable.
 *
 * @param {string} slug
 * @param {'en'|'fr'|string} locale
 * @param {{ en?: Record<string, unknown>, fr?: Record<string, unknown> }} [routesByLocale]
 *        Optional locale→routes map for tests / injection. When omitted, uses routes.json.
 * @returns {string|null}
 */
export const promoKeyFromRestaurantSlug = (slug, locale, routesByLocale) => {
  const trimmed = typeof slug === "string" ? slug.trim() : "";
  if (!trimmed) return null;

  const lang = locale === "fr" ? "fr" : "en";
  const routes =
    routesByLocale?.[lang] ?? (lang === "fr" ? routesFr.routes : routesEn.routes);

  for (const [promoKey, path] of Object.entries(routes)) {
    if (typeof path !== "string") continue;
    const segment = path
      .replace(/^\/fr\/promotions\/restaurants\//, "")
      .replace(/^\/promotions\/restaurants\//, "")
      .split("?")[0];
    if (segment === trimmed) return promoKey;
  }

  return null;
};

/**
 * Resolve DynamicDiningPromo page data for runtime routes.
 * Payload when `restaurant-promotions` is enabled; JSON locales as fallback.
 *
 * @param {{ pathname: string }} location
 * @param {string} locale
 * @returns {Promise<{ mode: 'payload', page: ReturnType<typeof mapRestaurantPromoDoc> } | { mode: 'json', promoKey: string } | null>}
 */
export async function resolveRestaurantPromoPage(location, locale) {
  const matched = matchRestaurantPromoPath(location?.pathname);
  if (!matched?.slug) return null;

  const { slug, locale: pathLocale } = matched;
  const lang = pathLocale === "fr" ? "fr" : "en";

  if (isPayloadSource("restaurant-promotions")) {
    try {
      const page = await fetchRestaurantPromoBySlug(slug, lang);
      if (isMeaningfulRestaurantPromo(page)) {
        return { mode: "payload", page };
      }
    } catch (error) {
      console.error(
        "Payload restaurant-promo fetch failed; falling back to JSON locales",
        error,
      );
    }
  }

  const promoKey = promoKeyFromRestaurantSlug(slug, lang);
  if (!promoKey) return null;

  return { mode: "json", promoKey };
}
